/**
 * Grundlage der Änderung direkt in einer Zeile (`InlineDatePicker`, `InlineUserPicker`) – jedes
 * Feld, das sich in einer Tabelle oder Liste ohne Detail ändern lässt, geht hierüber, damit alle
 * gleich aussehen und sich gleich bedienen:
 *
 * - In Ruhe steht nur der Wert da, ohne Rahmen.
 * - Beim Überfahren und mit Tastaturfokus wird er zum Feld: weißer Grund, feiner Rahmen, rechts
 *   blendet ein Pfeil ein – bei jeder Auswahl derselbe, auch beim Datum, das oft schon ein
 *   Kalender-Icon davor trägt. Kein Tooltip – das Feld selbst zeigt, dass es sich ändern lässt.
 *   Was es ist, steht als `label` über der geöffneten Auswahl. Auch der Tooltip für
 *   abgeschnittenen Text in Tabellen entfällt hier.
 * - Ein Klick, Enter oder Leertaste öffnet die Auswahl an ihm; solange sie offen ist, bleibt es
 *   als Feld markiert. Der Klick reicht nicht bis zur Zeile durch, die sonst ihr Detail öffnen
 *   würde.
 *
 * - Es steht dort, wo der Wert ohne Feld stünde: in einer zentrierten Spalte mittig, in einer
 *   linksbündigen links – die Ausrichtung kommt von der Zelle, nicht vom Aufrufer.
 *
 * Ohne Recht bleibt die Anzeige, wie sie ist.
 */
import React from "react";
import { Box, Typography } from "@mui/material";
import { KeyboardArrowDown as ArrowIcon } from "@mui/icons-material";
import { colors } from "../../../styles/colors";

/** Welche Auswahl ein Klick öffnet – für Screenreader */
export type InlineEditableKind = "date" | "select";

/**
 * Kennzeichnet das Feld im DOM: Der Tooltip für abgeschnittenen Text (`AbgeschnittenerInhalt`)
 * lässt es aus – er wiederholte nur den Wert, der schon dasteht.
 */
export const INLINE_EDITABLE_SELECTOR = "[data-inline-editable]";
const INLINE_EDITABLE_MARKER = { "data-inline-editable": "" };

/** Platz des Icons im Feld; in Ruhe ist die Breite 0, damit der Wert ohne Lücke steht */
const ICON_SLOT = 18;

const fieldStateSx = {
  backgroundColor: colors.background.paper,
  boxShadow: `inset 0 0 0 1px ${colors.border.light}`,
  "& .inline-editable-icon": { width: ICON_SLOT, opacity: 1 },
};

const inlineEditableSx = {
  // So breit wie der Wert, höchstens wie die Zelle: als Flex-Element im Rahmen darum schrumpft
  // es mit Ellipse, statt über die Zelle hinauszuragen
  display: "flex",
  flex: "0 1 auto",
  alignItems: "center",
  minWidth: 0,
  cursor: "pointer",
  borderRadius: 1,
  px: 0.75,
  mx: -0.75,
  py: 0.25,
  outline: "none",
  transition: "background-color 0.15s ease, box-shadow 0.15s ease",
  "&:hover": fieldStateSx,
  "&:focus-visible": {
    ...fieldStateSx,
    boxShadow: `inset 0 0 0 1px ${colors.border.focus}, 0 0 0 3px ${colors.shadow.focus}`,
  },
  "&[aria-expanded='true']": {
    ...fieldStateSx,
    boxShadow: `inset 0 0 0 1px ${colors.border.focus}`,
  },
};

const iconSlotSx = {
  display: "flex",
  justifyContent: "flex-end",
  flexShrink: 0,
  width: 0,
  opacity: 0,
  overflow: "hidden",
  transition: "width 0.15s ease, opacity 0.15s ease",
};

export interface InlineEditableProps {
  editable: boolean;
  /** Was ein Klick ändert, für Screenreader – etwa „Verantwortlichen ändern: Lars Roddewig“ */
  ariaLabel: string;
  /** Was geändert wird („Geplante Genehmigung“) – steht als Überschrift über der Auswahl */
  label?: string;
  children: React.ReactNode;
}

const InlineEditable: React.FC<
  Omit<InlineEditableProps, "label"> & {
    kind: InlineEditableKind;
    open: boolean;
    onOpen: (anchor: HTMLElement) => void;
  }
> = ({ editable, ariaLabel, kind, open, onOpen, children }) => {
  if (!editable)
    return (
      <Box {...INLINE_EDITABLE_MARKER} sx={{ minWidth: 0 }}>
        {children}
      </Box>
    );
  return (
    // Inline statt Block: Das Feld folgt der Ausrichtung seiner Zelle (`align` der Spalte) wie
    // ein Text. Als Block stünde es auch unter einer zentrierten Überschrift links.
    <Box
      {...INLINE_EDITABLE_MARKER}
      sx={{
        display: "inline-flex",
        maxWidth: "100%",
        minWidth: 0,
        verticalAlign: "middle",
      }}
    >
      <Box
        component="span"
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-haspopup={kind === "date" ? "dialog" : "menu"}
        aria-expanded={open}
        onClick={(event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          onOpen(event.currentTarget);
        }}
        onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          event.stopPropagation();
          onOpen(event.currentTarget);
        }}
        sx={inlineEditableSx}
      >
        <Box sx={{ minWidth: 0, flex: "0 1 auto" }}>{children}</Box>
        <Box className="inline-editable-icon" sx={iconSlotSx}>
          <ArrowIcon sx={{ fontSize: 14, color: colors.text.secondary }} />
        </Box>
      </Box>
    </Box>
  );
};

/** Überschrift über der geöffneten Auswahl, wenn das Feld ein `label` hat */
export const InlineEditableHeading: React.FC<{ label?: string }> = ({
  label,
}) =>
  label ? (
    <Typography
      variant="caption"
      component="div"
      sx={{ px: 2, pt: 1.5, pb: 0.5, color: colors.text.secondary }}
    >
      {label}
    </Typography>
  ) : null;

export default InlineEditable;
